import { useMemo } from "react";
import { useInfiniteQuery } from "react-query";
import { formsApi } from "@circle-react/api/formsApi";
import type { Form } from "@circle-react/types/Form";

interface PageProps {
  count: number;
  has_next_page: boolean;
  page: number;
  page_count: number;
  per_page: number;
  records: Form[];
}

interface UseListFormsInfiniteQueryProps {
  initialPage?: number;
  perPage?: number;
}

export const DEFAULT_PAGE_SIZE = 20;

export const useListFormsInfiniteQuery = ({
  perPage = DEFAULT_PAGE_SIZE,
  initialPage,
}: UseListFormsInfiniteQueryProps) => {
  const fetchPage = ({ pageParam = initialPage }) => {
    let page = 1;

    if (typeof pageParam === "number" && pageParam > 0) {
      page = pageParam;
    }

    return formsApi.index({ page, per_page: perPage });
  };

  const queryKey = ["forms", "list", perPage, initialPage];

  const queryOptions = {
    getNextPageParam: ({ has_next_page, page }: PageProps) =>
      has_next_page ? Number(page) + 1 : undefined,
    getPreviousPageParam: ({ page }: any) => (page > 1 ? page - 1 : undefined),
    keepPreviousData: true,
  };

  const result = useInfiniteQuery(queryKey, fetchPage, queryOptions);
  const pages: PageProps[] = useMemo(
    () => result.data?.pages ?? [],
    [result.data?.pages],
  );
  const totalRecords = pages[0]?.count ?? 0;
  const pageSize = pages[0]?.per_page ?? DEFAULT_PAGE_SIZE;

  return {
    ...result,
    queryKey,
    totalRecords,
    pageSize,
    pages,
  };
};
